import { useParams, Navigate } from "react-router-dom";
const SUPPORTED_LANGS = ["en", "es", "fr", "de", "pt", "ar", "ja", "hi", "zh", "he"];
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { supabase } from "@/integrations/supabase/client";
import BlogArticleLayout from "@/components/BlogArticleLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import "./DynamicBlogPost.css";

const DynamicBlogPost = () => {
  const { slug, lang } = useParams<{ slug: string; lang?: string }>();
  const { t, i18n } = useTranslation();
  const currentLang = lang || i18n.language || "en";

  const { data: post, isLoading, error } = useQuery({
    queryKey: ["blog-post", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts" as any)
        .select("*")
        .eq("slug", slug)
        .eq("published", true)
        .single();
      if (error) throw error;
      return data as any;
    },
    enabled: !!slug,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="container mx-auto max-w-4xl space-y-4">
          <Skeleton className="h-10 w-3/4" />
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </div>
    );
  }

  if (error || !post) {
    return <Navigate to="/blog" replace />;
  }

  const translations = post.translations as Record<string, { title: string; description: string; content: string }>;
  const content = translations[currentLang] || translations["en"];

  if (!content) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <>
      <Helmet>
        <title>{content.title} | Call24x7.AI</title>
        <meta name="description" content={content.description} />
        <link rel="canonical" href={`https://call24x7.ai/blog/${currentLang !== "en" ? `${currentLang}/` : ""}${slug}`} />
        <meta name="robots" content="index, follow" />
        {/* Hreflang tags for multilingual SEO */}
        <link rel="alternate" hrefLang="x-default" href={`https://call24x7.ai/blog/${slug}`} />
        {SUPPORTED_LANGS.map(lang => (
          <link key={lang} rel="alternate" hrefLang={lang} href={`https://call24x7.ai/blog/${lang === "en" ? "" : `${lang}/`}${slug}`} />
        ))}
        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={content.title} />
        <meta property="og:description" content={content.description} />
        <meta property="og:url" content={`https://call24x7.ai/blog/${slug}`} />
        {post.image_url && <meta property="og:image" content={post.image_url} />}
        <meta property="og:site_name" content="Call24x7.AI" />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={content.title} />
        <meta name="twitter:description" content={content.description} />
        {post.image_url && <meta name="twitter:image" content={post.image_url} />}
        {/* Article metadata */}
        <meta property="article:published_time" content={post.date} />
        <meta property="article:author" content={post.author || "Call24x7 Team"} />
        <meta property="article:section" content={post.category} />
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": content.title,
          "description": content.description,
          "image": post.image_url || undefined,
          "author": { "@type": "Organization", "name": "Call24x7.AI" },
          "publisher": { "@type": "Organization", "name": "Call24x7.AI", "url": "https://call24x7.ai" },
          "datePublished": post.date,
          "dateModified": post.updated_at || post.date,
          "mainEntityOfPage": { "@type": "WebPage", "@id": `https://call24x7.ai/blog/${slug}` },
          "url": `https://call24x7.ai/blog/${slug}`,
          "inLanguage": currentLang,
        })}</script>
      </Helmet>

      <BlogArticleLayout
        title={content.title}
        category={post.category}
        date={post.date}
        readTime={post.read_time}
        heroImage={post.image_url || "/placeholder.svg"}
        heroAlt={content.title}
      >
        <div 
          className="dynamic-blog-content prose prose-lg dark:prose-invert max-w-none"
          dangerouslySetInnerHTML={{ __html: content.content }} 
        />

        <Card className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20 mt-8">
          <CardContent className="pt-6 text-center">
            <h3 className="text-xl font-bold mb-2">{t('blog.whatIsAIAnsweringService.ctaTitle', 'Ready to Try AI Customer Support?')}</h3>
            <p className="text-muted-foreground mb-4">{t('blog.whatIsAIAnsweringService.ctaDesc', 'Build your AI answering service in 60 seconds.')}</p>
            <Button onClick={() => window.location.href = "https://manage.call24x7.ai/signup"}>
              {t('blog.whatIsAIAnsweringService.ctaButton', 'Start Free Trial')}
            </Button>
          </CardContent>
        </Card>
      </BlogArticleLayout>
    </>
  );
};

export default DynamicBlogPost;
